import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { AppBadges } from "@/components/AppBadges";
import { Heart, ShieldCheck, Lock, Sparkles } from "lucide-react";

const VALUES = [
  {
    icon: Heart,
    title: "Family first",
    desc: "Every decision starts with one question: does this help families care for each other better?",
  },
  {
    icon: Lock,
    title: "Privacy by design",
    desc: "Your health data is yours. We encrypt it, never sell it, and put you in control of what's shared.",
  },
  {
    icon: ShieldCheck,
    title: "Safety you can trust",
    desc: "From Emergency SOS to medical passports, we build for the moments that matter most.",
  },
  {
    icon: Sparkles,
    title: "Simple for everyone",
    desc: "Elegant, accessible design that works for every generation — from teens to grandparents.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About"
        description="Kinect Nova AI is on a mission to keep families connected, healthy and protected with one beautifully simple app."
        path="/about"
      />
      <PageHeader
        eyebrow="About us"
        title="We help families care for each other"
        subtitle="Kinect Nova AI was born from a simple belief: caring for the people you love shouldn't be complicated, stressful or scattered across a dozen apps."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Our mission
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>
                Families today are spread across cities and time zones, juggling
                medications, appointments and the constant worry of "is everyone okay?".
                We set out to replace that worry with confidence.
              </p>
              <p>
                Kinect Nova AI unifies medication management, emergency response, health
                tracking and communication into one trusted hub — so coordinating care
                feels effortless, and help is always one tap away.
              </p>
              <p>
                We're a team of engineers, designers and caregivers building the tool we
                wish our own families had.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-3xl border border-border shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1760084836111-b384c6d32104?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="A happy family spending time together outdoors"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-20">
          <Reveal>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              What we value
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal
                  key={v.title}
                  delay={i * 0.06}
                  className="rounded-3xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-foreground">{v.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-muted-foreground">{v.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="mt-20 rounded-[2rem] bg-primary px-8 py-14 text-center sm:px-16">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl">
            Join families who choose peace of mind
          </h2>
          <div className="mt-7 flex justify-center">
            <AppBadges className="justify-center" />
          </div>
        </div>
      </section>
    </>
  );
}
